import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, EffectFade, Controller } from 'swiper/modules';
import GLightbox from "glightbox";
import { createApp } from 'vue';
import NavigationSubmenu from './components/NavigationSubmenu.vue';
import NavigationMobile from './components/NavigationMobile.vue';
import SearchOverlay from './components/SearchOverlay.vue';
// import "./subnavigation.js";
import "./partner-map.js";
import "./job-advertisement.js";
import "./camera-selector.js";
import "./accessories-selector.js";
import "./countup.js";
import "./bookmark-button.js";
import "./camera-accessories-selection.js";
import "./camera-comparison.js";
import "./framerate-calculator-modal.js";
import "./ribbon.js";
import "./software-downloads.js";
import "./firmware-downloads.ts";
import "./expand-all-details.js";
import "./accordion-animation.js";

document.addEventListener("DOMContentLoaded", function (event) {

    // LOAD ALL MENU DATA ONCE (for both desktop submenu and mobile navigation)
    loadAndInitNavigation();

    // HIGHLIGHT TEASER: sync margin-top/padding-top to actual image height
    initHighlightTeasers();

    // INIT SEARCH OVERLAY (Vue Component)
    initSearchOverlay();

    // UPDATE BOOKMARK COUNT
    updateBookmarkCount();

    // SHOW LEGACY BANNER IF QUERY PARAMETER IS PRESENT
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('legacy') === '1') {
        document.body.classList.add('show-legacy-banner');
        // Remove the parameter from the URL without reloading the page
        urlParams.delete('legacy');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, document.title, newUrl);

        document.querySelector('[data-behaviour="close-legacy-banner"]').addEventListener('click', function(e) {
            e.preventDefault();
            document.body.classList.remove('show-legacy-banner');
        });
    }

    // INIT LIGHTBOX
    const lightbox = GLightbox({
        selector: '[data-gallery="gallery"]',
        touchNavigation: true,
        loop: true,
    });

    // INIT SWIPER
    const sliders = document.querySelectorAll('.swiper');
    sliders.forEach(function (slider) {
        const autoSwiper = slider.classList.contains('swiper--auto');
        const swiper = new Swiper(slider, {
            // configure Swiper to use modules
            modules: [Pagination, Navigation, Autoplay, EffectFade],
            effect: 'slide',
            fadeEffect: {
                crossFade: true
            },
            direction: 'horizontal',
            loop: true,

            autoplay: autoSwiper ? {
                delay: 5000,
            } : false,

            // Navigation arrows
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },

            pagination: {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true,
            },
        });
    });

    // FIXED HEADER
    window.addEventListener('scroll', () => {
        if (document.documentElement.scrollTop > 30 || document.body.scrollTop > 30){
            document.body.classList.add('menu--fixed');
        } else {
            document.body.classList.remove('menu--fixed');
        }
    });

    //Hero Swiper
    const heroSwipers = document.querySelectorAll('.heroswiper');
    if(heroSwipers.length > 0){
        heroSwipers.forEach(function (heroSwiper) {
            const buttonNext = heroSwiper.querySelector('.heroswiper-next');
            const buttonPrev = heroSwiper.querySelector('.heroswiper-prev');
            const videos = heroSwiper.querySelectorAll('video');
            let videopaused = false;

            // Video play/pause button
            const videoPauseButtons = heroSwiper.querySelectorAll('.video-pause');
            videoPauseButtons.forEach(function (btn) {
                btn.addEventListener('click', function() {
                    if(videopaused) {
                        videopaused = false;
                        videos.forEach(function(video) {
                            video.play();
                        });
                        btn.classList.remove('video-pause--paused');
                    } else {
                        videos.forEach(function(video) {
                            video.pause();
                        });
                        videopaused = true;
                        btn.classList.add('video-pause--paused');
                    }
                });
            });

            // Initialize two to three Swipers that work together
            let mainSwiper = new Swiper(heroSwiper.querySelector('.heroswiper-image'), {
                modules: [Autoplay, EffectFade, Navigation, Pagination, Controller],
                effect: 'slide',
                fadeEffect: {
                    crossFade: true
                },
                direction: 'horizontal',
                loop: true,
                navigation: {
                    nextEl: buttonNext,
                    prevEl: buttonPrev,
                },
                pagination: {
                    el: '.swiper-pagination',
                    type: 'bullets',
                    clickable: true,
                }
            });

            let textSwiper = new Swiper(heroSwiper.querySelector('.heroswiper-text'), {
                modules: [Autoplay, EffectFade, Navigation, Pagination, Controller],
                effect: 'fade',
                fadeEffect: {
                    crossFade: true
                },
                direction: 'horizontal',
                loop: true,
                clickable: false,
                draggable: false,
                allowTouchMove: false,
            });


            const reflectionSwiperElement = heroSwiper.querySelector('.heroswiper-reflection');
            let reflectionSwiper = null;
            if(reflectionSwiperElement){
                reflectionSwiper = new Swiper(reflectionSwiperElement, {
                    modules: [Controller, EffectFade],
                    effect: 'slide',
                    fadeEffect: {
                        crossFade: true
                    },
                    direction: 'horizontal',
                    loop: true,
                    clickable: false,
                    draggable: false,
                    allowTouchMove: false,
                });
            }

            // mainSwiper controls the others (one-way synchronization)
            if(reflectionSwiper){
                mainSwiper.controller.control = [textSwiper, reflectionSwiper];
            } else {
                mainSwiper.controller.control = textSwiper;
            }
        });
    }
});

/**
 * Load all menu data once and initialize both desktop and mobile navigation
 */
async function loadAndInitNavigation() {
    const submenuContainer = document.getElementById('nav-submenu-container');
    const mobileNavContainer = document.getElementById('nav-mobile-container');

    // Get API URL from either container (both should have the same URL)
    const apiUrl = submenuContainer?.dataset.apiUrl || mobileNavContainer?.dataset.apiUrl;

    if (!apiUrl) {
        console.error('No API URL found for navigation');
        return;
    }

    // Initialize submenu immediately with empty data (adds event listeners)
    // This ensures hover events are captured even while data is loading
    const submenuCallbacks = initNavigationSubmenu(null, apiUrl);

    let allMenusData = null;

    // Load menu data once
    try {
        const response = await fetch(apiUrl);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        allMenusData = await response.json();
        // console.log('All menu data loaded once for desktop and mobile');
    } catch (err) {
        console.error('Failed to load menu data:', err);
        return;
    }

    // Update submenu with loaded data and show pending hover if any
    if (submenuCallbacks) {
        submenuCallbacks.setMenuData(allMenusData);
    }

    // Initialize mobile navigation with the loaded data
    initNavigationMobile(allMenusData, apiUrl);
}

/**
 * Initialize navigation submenu with preloaded data
 * Returns callbacks to update data after loading
 */
function initNavigationSubmenu(allMenusData, apiUrl) {
    const navLinks = document.querySelectorAll('.nav_link[data-has-submenu="true"]');
    const container = document.getElementById('nav-submenu-container');
    let currentVueApp = null;
    let currentMenuId = null;
    let pendingHoverMenuId = null; // Track hover that happened before data loaded
    let hoverTimeout = null;
    const menuDataCache = new Map();
    let dataLoaded = false;
    let baseUrl = '';

    if (!container) return null;

    if (apiUrl) {
        // Extract base URL (remove /allsubmenus from end)
        baseUrl = apiUrl.replace(/\/allsubmenus$/, '');
    }

    // If data is already provided, populate cache immediately
    if (allMenusData) {
        populateCache(allMenusData);
        dataLoaded = true;
    }

    function populateCache(data) {
        // Handle both old format (direct menus) and new format (with translations)
        const menus = data.menus || data;
        const translations = data.translations || { goToPage: 'Go to page' };

        // Populate cache from preloaded response
        for (const [menuId, menuData] of Object.entries(menus)) {
            menuDataCache.set(parseInt(menuId), { ...menuData, translations });
        }
    }

    // Add event listeners immediately (even before data is loaded)
    navLinks.forEach(link => {
        const menuId = parseInt(link.dataset.menuId.replace('menu-', ''));

        // Show submenu on hover
        link.addEventListener('mouseenter', () => {
            clearTimeout(hoverTimeout);

            // Small delay to prevent flickering
            hoverTimeout = setTimeout(() => {
                if (dataLoaded) {
                    showSubmenu(menuId);
                } else {
                    // Data not loaded yet - remember which menu was hovered
                    pendingHoverMenuId = menuId;
                }
            }, 150);
        });

        // Keep submenu open when hovering over it
        link.addEventListener('mouseleave', (e) => {
            clearTimeout(hoverTimeout);
            pendingHoverMenuId = null; // Clear pending hover when mouse leaves

            hoverTimeout = setTimeout(() => {
                const submenu = container.querySelector('.nav_submenu');
                if (!submenu || !submenu.matches(':hover')) {
                    hideSubmenu();
                }
            }, 300);
        });
    });

    // Hide submenu when mouse leaves the submenu area
    container.addEventListener('mouseleave', () => {
        clearTimeout(hoverTimeout);
        pendingHoverMenuId = null;
        hoverTimeout = setTimeout(() => {
            hideSubmenu();
        }, 300);
    });

    function showSubmenu(menuId) {
        // Don't recreate if same menu, just make it visible again
        if (currentMenuId === menuId && currentVueApp) {
            const submenu = container.querySelector('.nav_submenu');
            if (submenu) {
                submenu.classList.add('active');
            }
            return;
        }

        // Cleanup previous Vue app
        if (currentVueApp) {
            currentVueApp.unmount();
            currentVueApp = null;
        }

        // Get preloaded data
        const menuData = menuDataCache.get(menuId);

        if (!menuData) {
            console.warn('No menu data found for menuId:', menuId);
            return;
        }

        // Create new Vue app with preloaded data
        currentVueApp = createApp(NavigationSubmenu, {
            menuId: menuId,
            currentUrl: baseUrl,
            preloadedData: menuData // Pass preloaded data
        });

        container.innerHTML = '';
        currentVueApp.mount(container);
        currentMenuId = menuId;
    }

    function hideSubmenu() {
        // Find the nav_submenu element and remove active class
        const submenu = container.querySelector('.nav_submenu');
        if (submenu) {
            submenu.classList.remove('active');
        }
    }

    // Return callbacks to update data after loading
    return {
        setMenuData: (data) => {
            if (data) {
                populateCache(data);
                dataLoaded = true;

                // If user was hovering while data was loading, show that menu now
                if (pendingHoverMenuId !== null) {
                    showSubmenu(pendingHoverMenuId);
                    pendingHoverMenuId = null;
                }
            }
        }
    };
}

/**
 * Update bookmark count badge in header
 * Exported globally so it can be called from Vue components
 */
export async function updateBookmarkCount() {
    const bookmarkLink = document.querySelector('[data-bookmark-link]');
    const bookmarkBadge = document.querySelector('[data-bookmark-count]');

    if (!bookmarkLink || !bookmarkBadge) {
        return;
    }

    const apiUrl = bookmarkLink.dataset.countApiUrl;

    if (!apiUrl) {
        console.error('No count API URL found on bookmark link');
        return;
    }

    try {
        const response = await fetch(apiUrl);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && typeof data.count !== 'undefined') {
            bookmarkBadge.textContent = data.count > 0 ? `(${data.count})` : '';
        }
    } catch (err) {
        console.error('Failed to load bookmark count:', err);
    }
}

// Make function globally accessible
window.updateBookmarkCount = updateBookmarkCount;

function initHighlightTeasers() {
    const sections = document.querySelectorAll('.section--HighlightTeaserElement');
    if (!sections.length) return;

    const updateSection = (section) => {
        const entries = Array.from(section.querySelectorAll('.teaserlist_entry'));
        if (!entries.length) return;

        // First pass: measure each entry's image half-height and bottom edge
        const data = entries.map(entry => {
            const imageEl = entry.querySelector('.teaserlist_entry_image');
            const halfH = imageEl ? (imageEl.offsetHeight / 2 + 10) : 0;
            const bottom = Math.round(entry.getBoundingClientRect().bottom);
            return { entry, halfH, bottom };
        });

        // Group entries into rows by shared bottom edge (flex-end alignment)
        const rows = new Map();
        data.forEach(item => {
            let rowKey = null;
            for (const key of rows.keys()) {
                if (Math.abs(key - item.bottom) < 2) {
                    rowKey = key;
                    break;
                }
            }
            if (rowKey === null) {
                rowKey = item.bottom;
                rows.set(rowKey, []);
            }
            rows.get(rowKey).push(item);
        });

        // Apply the row's max --img-half-height to every entry in that row
        rows.forEach(rowItems => {
            const maxHalfH = Math.max(...rowItems.map(d => d.halfH));
            rowItems.forEach(({ entry }) => {
                const overlap = Math.min(maxHalfH, 100);
                entry.style.setProperty('--img-half-height', `${maxHalfH}px`);
                entry.style.setProperty('--img-overlap', `${overlap}px`);
            });
        });
    };

    const observer = new ResizeObserver(() => sections.forEach(updateSection));

    sections.forEach(section => {
        section.querySelectorAll('.teaserlist_entry_image').forEach(imageEl => {
            observer.observe(imageEl);
        });
    });
}

/**
 * Initialize search overlay
 */
function initSearchOverlay() {
    const searchInput = document.querySelector('.nav_search_input');
    const searchOverlayContainer = document.getElementById('search-overlay-container');

    if (!searchOverlayContainer) {
        console.error('Search overlay container not found');
        return;
    }

    // Create Vue app for search overlay
    const searchOverlayApp = createApp(SearchOverlay, {
        apiUrl: '/typesense',
        locale: document.documentElement.lang
    });

    const instance = searchOverlayApp.mount(searchOverlayContainer);

    // Make instance globally available
    window.openSearchOverlay = () => {
        instance.open();
    };

    // Open overlay when search input is focused
    if (searchInput) {
        searchInput.addEventListener('focus', (e) => {
            e.preventDefault();
            instance.open();
            // Blur the original input
            searchInput.blur();
        });
    }

    // Also add click handler for search icon if it exists
    const searchIcon = document.querySelector('.nav_search_icon');
    if (searchIcon) {
        searchIcon.addEventListener('click', (e) => {
            e.preventDefault();
            instance.open();
        });
    }
}

/**
 * Initialize mobile navigation with preloaded data
 */
function initNavigationMobile(allMenusData, apiUrl) {
    const menuButton = document.querySelector('[data-behaviour="toggle-menu"]');
    const mobileNavContainer = document.getElementById('nav-mobile-container');

    if (!menuButton || !mobileNavContainer) {
        console.error('Mobile navigation container or button not found');
        return;
    }

    if (!apiUrl || !allMenusData) {
        console.error('No API URL or menu data provided to initNavigationMobile');
        return;
    }

    // Handle both old format (direct menus) and new format (with translations)
    const menuData = allMenusData.menus || allMenusData;

    // console.log('Mobile menu initialized with', Object.keys(menuData).length, 'items');

    // Create Vue app for mobile navigation
    const mobileNavApp = createApp(NavigationMobile, {
        menuData: menuData,
        translations: allMenusData.translations || { goToPage: 'Go to page' },
        orderPageLink: mobileNavContainer.dataset.orderPageLink,
        onClose: () => {
            document.body.classList.remove('body--show');
        },
        onOpenSearch: () => {
            // Open search overlay when search icon is clicked in mobile nav
            if (window.openSearchOverlay) {
                window.openSearchOverlay();
            }
        }
    });

    const instance = mobileNavApp.mount(mobileNavContainer);

    // Toggle mobile menu when button is clicked
    menuButton.addEventListener('click', () => {
        instance.toggle();
        document.body.classList.toggle('body--show');
    });
}

